import { useEffect, useRef, useState, type RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Box, Grid, Button, Checkbox, FormControl, FormLabel, HStack, Input, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, Portal, Spinner, Text, VStack } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/contexts/AuthContext'
import { systemService } from '@/services/admin/systemService'
import { merchantUserInvitationService as service, type QuotaTarget, type QuotaSetting,
  type InvitationQuota } from '@/services/admin/merchantUserInvitationService'

export function UserInvitationPolicies({ canWrite, refreshContainerRef }: {
  canWrite: boolean; refreshContainerRef?: RefObject<HTMLDivElement>;
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const { checkPermission } = useAuth()
  const failureKey = (error?: unknown) =>
    (error as { response?: { status?: number } } | null)?.response?.status === 503 ? 'unavailable' : 'failed'
  const notifyFailure = (error?: unknown) => toast.error({ title: t(`userInvitations.${failureKey(error)}`) })
  const [target, setTarget] = useState<QuotaTarget>({ scope: 'plan' })
  const [targetInput, setTargetInput] = useState('')
  const [setting, setSetting] = useState<QuotaSetting['kind']>('limited')
  const [max, setMax] = useState('')
  const [applyTo, setApplyTo] = useState<'inheriting' | 'all'>('inheriting')
  const [amount, setAmount] = useState('')
  const [count, setCount] = useState('')
  const [busy, setBusy] = useState(false)
  const writing = useRef(false)
  const [confirmation, setConfirmation] = useState<'quota' | 'eligibility' | null>(null)
  const [quotaLoaded, setQuotaLoaded] = useState(false)
  const [requirementsLoaded, setRequirementsLoaded] = useState(false)
  const [configured, setConfigured] = useState<boolean | null>(null)
  const key = `${target.scope}:${target.targetId ?? ''}`
  const selected = useRef(key)
  selected.current = key
  const changeTarget = (next: QuotaTarget) => {
    if (writing.current) return
    selected.current = `${next.scope}:${next.targetId ?? ''}`
    setTarget(next); setQuotaLoaded(false); setRequirementsLoaded(false); setConfigured(null)
    setConfirmation(null); setApplyTo('inheriting'); setMax(''); setAmount(''); setCount('')
  }
  const queryWithToast = async <T,>(action: () => Promise<T>): Promise<T> => {
    try { return await action() } catch (error) {
      if (selected.current === key) notifyFailure(error)
      throw error
    }
  }
  const searchUser = async () => {
    if (writing.current || busy) return
    changeTarget({ scope: 'user' })
    if (!checkPermission('users:read')) {
      toast.error({ title: t('userInvitations.emailPermission') }); return
    }
    writing.current = true; setBusy(true)
    try {
      const user = await service.resolveUserEmail(targetInput)
      setTarget({ scope: 'user', targetId: user.userId })
      setTargetInput(user.email)
      toast.success({ title: t('userInvitations.userFound') })
    } catch (error) {
      const key = error instanceof Error && error.message === 'INVITATION_EMAIL_INVALID' ? 'invalidEmail'
        : error instanceof Error && error.message === 'INVITATION_EMAIL_NOT_FOUND' ? 'emailNotFound' : failureKey(error)
      toast.error({ title: t(`userInvitations.${key}`) })
    } finally { writing.current = false; setBusy(false) }
  }
  const plans = useQuery({ queryKey: ['invitation-plan-options'],
    queryFn: () => queryWithToast(() => systemService.getSubscriptionPlans('mainnet')), enabled: target.scope === 'plan',
    retry: false, gcTime: 0 })
  useEffect(() => {
    if (target.scope !== 'plan' || target.targetId || plans.isError || plans.isFetching) return
    const firstPlan = plans.data?.[0]
    if (!firstPlan || typeof firstPlan.id !== 'string' || !firstPlan.id.trim()) return
    changeTarget({ scope: 'plan', targetId: firstPlan.id })
  }, [target.scope, target.targetId, canWrite, plans.data, plans.isError, plans.isFetching])
  const userQuota = useQuery({ queryKey: ['invitation-quota', target.scope, target.targetId],
    queryFn: () => queryWithToast(() => service.quota(target)), enabled: target.scope === 'user' && Boolean(target.targetId),
    retry: false, gcTime: 0 })
  const planSettings = useQuery({ queryKey: ['invitation-plan-settings', target.targetId],
    queryFn: () => queryWithToast(() => service.planSettings(target.targetId!)),
    enabled: target.scope === 'plan' && Boolean(target.targetId), retry: false, gcTime: 0 })
  const planData = planSettings.data?.planId === target.targetId ? planSettings.data : undefined
  const quotaData: InvitationQuota | undefined = target.scope === 'plan'
    ? planData?.configurationState === 'configured' ? {
      scope: 'plan', targetId: planData.planId, setting: planData.quota,
      effectiveLimit: planData.quota.kind === 'limited' ? planData.quota.maxUses : null,
      effectiveSource: 'plan', effectivePlanId: planData.planId, usage: null, planImpact: planData.planImpact,
    } : undefined : userQuota.data?.targetId === target.targetId ? userQuota.data : undefined
  const quota = { ...(target.scope === 'plan' ? planSettings : userQuota), data: quotaData }
  const requirements = { ...planSettings, data: planData?.eligibility ?? undefined }
  // Hydrate each selected target once; background refetches must not replace an editable draft.
  useEffect(() => {
    const data = quota.data
    if (target.scope === 'plan' && !quotaLoaded && !planSettings.isError && !planSettings.isFetching
      && planData?.configurationState === 'unconfigured') {
      setSetting('limited'); setMax(''); setAmount(''); setCount(''); setConfigured(false)
      setQuotaLoaded(true); setRequirementsLoaded(true); return
    }
    if (quotaLoaded || quota.isError || quota.isFetching || !data
      || data.scope !== target.scope || data.targetId !== (target.targetId ?? null)) return
    setSetting(data.setting.kind)
    setMax(data.setting.kind === 'limited' ? String(data.setting.maxUses) : '')
    setApplyTo('inheriting')
    setQuotaLoaded(true)
    if (target.scope === 'plan') setConfigured(true)
  }, [quota.data, quota.isError, quota.isFetching, quotaLoaded, target.scope, target.targetId,
    planData, planSettings.isError, planSettings.isFetching])
  useEffect(() => {
    if (target.scope !== 'plan' || requirementsLoaded || requirements.isError || requirements.isFetching || !requirements.data) return
    setAmount(requirements.data.minSettledAmountUsd)
    setCount(String(requirements.data.minSettledOrderCount))
    setRequirementsLoaded(true)
  }, [requirements.data, requirements.isError, requirements.isFetching, requirementsLoaded, target.scope])
  const loadQuota = (data: InvitationQuota) => {
    setSetting(data.setting.kind)
    setMax(data.setting.kind === 'limited' ? String(data.setting.maxUses) : '')
    setApplyTo('inheriting')
    setQuotaLoaded(true)
  }
  const readRequirements = async () => {
    const result = await planSettings.refetch()
    if (selected.current !== key) return
    if (result.data && result.data.planId === target.targetId && result.data.configurationState === 'configured' && !result.isError) {
      setAmount(result.data.eligibility.minSettledAmountUsd); setCount(String(result.data.eligibility.minSettledOrderCount))
      setRequirementsLoaded(true)
    } else { setRequirementsLoaded(false) }
  }
  const quotaPayload = (): QuotaSetting => {
    if (setting === 'inherit') return { kind: 'inherit' }
    if (setting === 'unlimited') return { kind: 'unlimited' }
    if (!/^\d+$/.test(max) || !Number.isSafeInteger(Number(max))) throw new Error('Invalid limit')
    return { kind: 'limited', maxUses: Number(max) }
  }
  const saveQuota = async () => {
    if (writing.current || busy || !quotaLoaded || !canWrite || quota.isError) return
    if (target.scope === 'plan' && configured !== true) return
    writing.current = true
    setBusy(true)
    try {
      const result = await service.setQuota(target, { ...quotaPayload(),
        ...(target.scope === 'plan' ? { applyTo } : {}) })
      loadQuota(result); toast.success({ title: t('userInvitations.quotaSaved') })
      await quota.refetch()
      setConfirmation(null)
    } catch (error) { notifyFailure(error); await quota.refetch() }
    finally { writing.current = false; setBusy(false); setConfirmation(null) }
  }
  const saveRequirements = async () => {
    if (writing.current || busy || !requirementsLoaded || !canWrite || requirements.isError) return
    if (target.scope !== 'plan' || !target.targetId || configured !== true) return
    writing.current = true
    setBusy(true)
    try {
      if (!/^(0|[1-9][0-9]*)(\.[0-9]{1,6})?$/.test(amount)
        || !/^\d+$/.test(count) || !Number.isSafeInteger(Number(count))) throw new Error('Invalid requirements')
      await service.setRequirements(target.targetId, { minSettledAmountUsd: amount, minSettledOrderCount: Number(count) })
      toast.success({ title: t('userInvitations.eligibilitySaved') })
      await readRequirements()
    } catch (error) { notifyFailure(error); await requirements.refetch() }
    finally { writing.current = false; setBusy(false); setConfirmation(null) }
  }
  const initializePlan = async () => {
    if (writing.current || busy || !canWrite || configured !== false || target.scope !== 'plan'
      || !target.targetId || planSettings.isError) return
    writing.current = true; setBusy(true)
    try {
      const value = quotaPayload()
      if (value.kind === 'inherit' || !/^(0|[1-9][0-9]*)(\.[0-9]{1,6})?$/.test(amount)
        || !/^\d+$/.test(count) || !Number.isSafeInteger(Number(count))) throw new Error('Invalid settings')
      await service.initializePlan(target.targetId, { quota: value,
        eligibility: { minSettledAmountUsd: amount, minSettledOrderCount: Number(count) } })
      setQuotaLoaded(false); setRequirementsLoaded(false); setConfigured(null)
      toast.success({ title: t('userInvitations.planSaved') })
      await planSettings.refetch()
    } catch (error) {
      notifyFailure(error)
      if ((error as { response?: { status?: number } })?.response?.status === 409) {
        setQuotaLoaded(false); setRequirementsLoaded(false); setConfigured(null)
      }
      await planSettings.refetch()
    } finally { writing.current = false; setBusy(false) }
  }
  const refresh = async () => {
    const result = await quota.refetch()
    if (selected.current !== key || result.isError) return
    setQuotaLoaded(false); setRequirementsLoaded(false); setConfigured(null)
    toast.success({ title: t('userInvitations.refreshed') })
  }
  const label = (key: string) => t(`userInvitations.${key}`)
  const settingLabel = (kind: string) => {
    if (kind === 'inherit') return label('userInherit')
    if (kind === 'individual') return label('userIndividual')
    return label(kind)
  }
  const refreshControl = target.targetId ? <RefreshIconButton label={t('common.refresh')}
    onClick={() => void refresh()} isDisabled={busy || quota.isFetching} /> : null
  return <VStack align="stretch" spacing={2}>
    {refreshContainerRef && <Portal containerRef={refreshContainerRef}>{refreshControl}</Portal>}
    <Grid templateColumns={{ base: 'minmax(0, 1fr)', xl: target.scope === 'plan'
      ? 'repeat(2, minmax(0, 1fr))' : 'minmax(0, 1fr)' }} gap={3}>
    <VStack align="stretch" spacing={2} minW={0}>
    <HStack spacing={2} flexWrap="wrap">
      <Text fontSize="sm" fontWeight="semibold" flexShrink={0}>{label(target.scope === 'plan' ? 'planLimits' : 'userLimit')}</Text>
      <DropdownSelect size="sm" value={target.scope} ariaLabel={label('scope')} isDisabled={busy}
        options={['plan','user'].map(value => ({ value, label: label(value) }))}
        onChange={value => { changeTarget({ scope: value as QuotaTarget['scope'] }); setTargetInput('') }} />
      {target.scope === 'plan' ? <DropdownSelect size="sm" value={target.targetId ?? ''} ariaLabel={label('plan')}
        options={(plans.data ?? []).map(plan => ({ value: plan.id, label: plan.displayName || plan.name }))}
        isDisabled={busy || plans.isError || plans.isFetching} placeholder={label('plan')}
        onChange={targetId => changeTarget({ scope: 'plan', targetId })} />
        : target.scope === 'user' && <><Input size="sm" w="240px" type="email" value={targetInput} onChange={e => {
          setTargetInput(e.target.value); changeTarget({ scope: 'user' })
        }}
          placeholder={label('email')} aria-label={label('email')} maxLength={254} isDisabled={busy} />
          <Button size="sm" onClick={() => void searchUser()}
            isDisabled={busy || !targetInput.trim()}>{label('load')}</Button></>}
      {quota.isFetching && !quotaLoaded && <Spinner size="sm" />}
      {target.scope === 'plan' && !plans.isError && !plans.isFetching && plans.data?.length === 0
        && <Text fontSize="sm">{label('noPlans')}</Text>}
      {target.scope === 'plan' && plans.isError && <RefreshIconButton label={t('common.refresh')}
        onClick={() => void plans.refetch()} isDisabled={busy || plans.isFetching} />}
      {!refreshContainerRef && refreshControl}
    </HStack>
    {target.scope === 'user' && quotaLoaded && quota.data && !quota.isError && <Text fontSize="sm">{label('effectiveUser')}: {quota.data.effectiveLimit ?? label('unlimited')}
      {' · '}{label('source')}: {label(quota.data.effectiveSource)}{quota.data.usage && ` · ${t('userInvitations.userUsage', {
        count: quota.data.usage.successfulInvites, remaining: quota.data.usage.remaining ?? label('unlimited'),
      })}`}</Text>}
    {configured === false && target.scope === 'plan' && !quota.isError && <Text fontSize="sm">{label('notConfigured')}</Text>}
    {quotaLoaded && !quota.isError && <VStack align="stretch" spacing={2}>
      {target.scope === 'user' && <DropdownSelect size="sm" value={setting === 'inherit' ? 'inherit' : 'individual'}
        ariaLabel={label('setting')} isDisabled={busy || !canWrite}
        options={['inherit','individual'].map(value => ({ value, label: settingLabel(value) }))}
        onChange={value => setSetting(value === 'inherit' ? 'inherit' : 'limited')} />}
      <HStack align="end" spacing={3}>
        <FormControl minW={0} flex={1} isDisabled={busy || !canWrite || setting !== 'limited'}>
          <FormLabel fontSize="xs" mb={1}>{label('limit')}</FormLabel>
          <Input size="sm" value={setting === 'limited' ? max : ''} onChange={e => setMax(e.target.value)}
            inputMode="numeric" aria-label={label('limit')}
            isDisabled={busy || !canWrite || setting !== 'limited'} />
        </FormControl>
        <Checkbox size="sm" colorScheme="blue" h="32px" flexShrink={0}
          isChecked={setting === 'unlimited'} isDisabled={busy || !canWrite || setting === 'inherit'}
          onChange={e => setSetting(e.target.checked ? 'unlimited' : 'limited')}>
          {label('unlimited')}
        </Checkbox>
      </HStack>
      {(target.scope === 'user' || configured) && <HStack align="end" spacing={2} flexWrap={{ base: 'wrap', sm: 'nowrap' }}>
      {target.scope === 'plan' && <FormControl minW={0} flex={1}>
        <FormLabel fontSize="xs" mb={1}>{label('applyTo')}</FormLabel>
        <DropdownSelect size="sm" value={applyTo} ariaLabel={label('applyTo')}
          isDisabled={busy || !canWrite} options={['inheriting','all'].map(value => ({ value,
            label: label(value === 'inheriting' ? 'inheriting' : 'planAll') }))}
          onChange={value => setApplyTo(value as typeof applyTo)} />
      </FormControl>}
      <Button size="sm" flexShrink={0} colorScheme="blue" isDisabled={busy || !canWrite || quota.isError} onClick={() => {
        try { quotaPayload() } catch { notifyFailure(); return }
        if ((target.scope === 'plan' && applyTo === 'all') || (target.scope === 'user' && setting === 'inherit')) setConfirmation('quota')
        else void saveQuota()
      }}>{label('saveLimit')}</Button>
      </HStack>}
    </VStack>}
    </VStack>
    {target.scope === 'plan' && <Box minW={0} borderLeftWidth={{ base: 0, xl: '1px' }} pl={{ base: 0, xl: 3 }}>
    <VStack align="stretch" spacing={2}>
    <HStack justify="space-between">
      <Text fontSize="sm" fontWeight="semibold">{label('planEligibility')}</Text>
      {requirements.isFetching && !requirementsLoaded && <Spinner size="sm" />}
      {!refreshContainerRef && requirements.isError && <RefreshIconButton label={t('common.refresh')}
        onClick={() => void refresh()} isDisabled={busy || requirements.isFetching} />}
    </HStack>
    {requirementsLoaded && !requirements.isError && <>
      <HStack align="end" spacing={2}><FormControl minW={0}><FormLabel fontSize="xs" mb={1}>{label('amount')}</FormLabel><Input size="sm" value={amount}
        onChange={e => setAmount(e.target.value)} isDisabled={busy || !canWrite} inputMode="decimal" /></FormControl>
        <FormControl minW={0}><FormLabel fontSize="xs" mb={1}>{label('orders')}</FormLabel><Input size="sm" value={count} onChange={e => setCount(e.target.value)}
          isDisabled={busy || !canWrite} inputMode="numeric" /></FormControl>
        {configured && <Button size="sm" flexShrink={0} colorScheme="blue" isDisabled={busy || !canWrite || requirements.isError}
          onClick={() => setConfirmation('eligibility')}>{label('saveEligibility')}</Button>}</HStack>
    </>}
    </VStack></Box>}
    </Grid>
    {target.scope === 'plan' && configured === false && !planSettings.isError && <Button size="sm" alignSelf="start"
      colorScheme="blue" isDisabled={busy || !canWrite} onClick={() => void initializePlan()}>{label('initializePlan')}</Button>}
    <Modal isOpen={confirmation !== null} onClose={() => { if (!busy) setConfirmation(null) }} closeOnOverlayClick={!busy}>
      <ModalOverlay /><ModalContent className="system-config-dialog"><ModalHeader>{label('confirm')}</ModalHeader><ModalBody>
        {confirmation === 'quota' ? <VStack align="stretch"><Text>{label(target.scope)}: {target.scope === 'user' ? targetInput
          : plans.data?.find(plan => plan.id === target.targetId)?.displayName ?? target.targetId} · {setting === 'limited'
            ? `${label('limit')}: ${max}` : settingLabel(setting)}</Text>
          {setting === 'limited' && max === '0' && <Text>{label('closed')}</Text>}
          {target.scope === 'plan' && <Text>{label('members')}: {quota.data?.planImpact?.memberUsers} · {label('overrides')}: {quota.data?.planImpact?.individualOverrides}</Text>}
          <Text>{label(target.scope === 'plan' ? 'clearOverrides' : 'restoreInheritance')}</Text></VStack>
          : <VStack align="stretch"><Text fontWeight="semibold">{label('planEligibility')}: {plans.data?.find(plan => plan.id === target.targetId)?.displayName ?? target.targetId}</Text>
            <Text>{label('planEligibilityImpact')}</Text><Text>{label('bothRequired')}</Text>
            <Text>{amount} USD · {count} {label('orders')}</Text><Text>{label('eligibilityNotice')}</Text>
            {(Number(amount) === 0 || Number(count) === 0) && <Text>{label('zeroMinimum')}</Text>}</VStack>}
      </ModalBody><ModalFooter gap={3}><Button isDisabled={busy} onClick={() => setConfirmation(null)}>{label('cancel')}</Button>
        <Button colorScheme="blue" isLoading={busy} onClick={() => void (confirmation === 'quota' ? saveQuota() : saveRequirements())}>{label('save')}</Button>
      </ModalFooter></ModalContent>
    </Modal>
  </VStack>
}
